/** @jsxImportSource @emotion/react */
import { css, keyframes } from "@emotion/react";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PublicIcon from "@mui/icons-material/Public";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SendIcon from "@mui/icons-material/Send";
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  Fragment,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
  VFC,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { CountryPath } from "src/entities/CountryPath";
import { AiArticlePreset } from "src/entities/ai/AiArticlePreset";
import {
  ChatArticleDraft,
  ChatMessageResponse,
} from "src/entities/v3/ChatMessageResponse";
import { useChatBusinessCategories } from "src/hooks/useChatBusinessCategories";
import { useChatMessage } from "src/hooks/useChatMessage";
import getUiLanguageName from "src/utils/getUiLanguageName";
import Button from "../components/common/Button";

interface Props {
  country: CountryPath;
}

interface ChatLine {
  role: "user" | "assistant";
  text: string;
  // Set on the user bubble whose send failed, to show recovery actions.
  error?: boolean;
}

/**
 * Best-effort extraction of the API error `message` field, falling back to a
 * generic i18n string.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractApiMessage = (err: any, fallback: string): string => {
  return String(err?.response?.data?.message || err?.message || fallback);
};

// Typing-dot "AI is thinking" animation.
const dotBlink = keyframes`
  0%, 80%, 100% { opacity: 0.2; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-3px); }
`;

const thinkingDot = css`
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: #0045ff;
  animation: ${dotBlink} 1.2s infinite ease-in-out both;
`;

/**
 * Render a single EditorJS block (header / paragraph) as preview HTML.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderBlock = (block: any, index: number) => {
  const text: string = block?.data?.text ?? "";
  if (block?.type === "header") {
    const level = block?.data?.level ?? 2;
    const className =
      level <= 2
        ? "mb-2 mt-4 text-lg font-bold"
        : "mb-2 mt-3 text-base font-bold";
    return (
      <div
        key={index}
        className={className}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: text }}
      />
    );
  }
  return (
    <p
      key={index}
      className="mb-3 text-sm leading-relaxed text-gray-700"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: text }}
    />
  );
};

const AiChatPage: VFC<Props> = (props: Props) => {
  const [t, i18n] = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { sendMessage } = useChatMessage();
  const { fetchCategories } = useChatBusinessCategories();

  const [messages, setMessages] = useState<ChatLine[]>([]);
  const [input, setInput] = useState("");
  const [previousInteractionId, setPreviousInteractionId] = useState<
    string | undefined
  >();
  const [article, setArticle] = useState<ChatArticleDraft | null>(null);
  const [businessCategories, setBusinessCategories] = useState<string[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Web search is opt-in (ChatGPT-style); off by default.
  const [webSearch, setWebSearch] = useState(false);
  // The auto kickoff send has no user bubble, so its failure is tracked here.
  const [kickoffFailed, setKickoffFailed] = useState(false);
  // Beta history notice is dismissible.
  const [historyNoticeOpen, setHistoryNoticeOpen] = useState(true);
  // "Start over" confirmation dialog.
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  // Cycling "AI is thinking" caption shown while sending.
  const [thinkingIndex, setThinkingIndex] = useState(0);

  // Generation counter: bumped on reset so stale in-flight responses are dropped.
  const genRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const thinkingMessages = [
    t("ai_chat.thinking_1"),
    t("ai_chat.thinking_2"),
    t("ai_chat.thinking_3"),
    t("ai_chat.thinking_4"),
  ];

  // Auto-scroll the chat to the latest message.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  // Cycle the thinking caption every few seconds while a request is in flight.
  useEffect(() => {
    if (!sending) {
      setThinkingIndex(0);
      return undefined;
    }
    const id = setInterval(() => {
      setThinkingIndex((i) => (i + 1) % thinkingMessages.length);
    }, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sending]);

  /**
   * Fetch business categories for a freshly generated draft.
   */
  const loadCategories = (draft: ChatArticleDraft) => {
    setCategoriesLoading(true);
    fetchCategories(draft, {
      onSuccess: (data) => {
        setBusinessCategories(data?.businessCategories ?? []);
      },
      onError: () => {
        // Do not block approval; surface the issue but allow manual selection.
        setBusinessCategories([]);
        enqueueSnackbar(t("ai_chat.categories_failed"), { variant: "warning" });
      },
      onEnd: () => {
        setCategoriesLoading(false);
      },
    });
  };

  /**
   * Send a message to the chat API.
   * @param message text to send
   * @param opts.addBubble whether to append a new user bubble for this message
   *   (false for the kickoff trigger and for retries, where the bubble already
   *   exists or is intentionally hidden)
   * @param opts.errorTarget where to surface a failure: mark the last user
   *   bubble ("bubble") or the kickoff retry block ("kickoff")
   */
  const runSend = (
    message: string,
    opts: {
      addBubble: boolean;
      errorTarget: "bubble" | "kickoff";
      freshContext?: boolean;
    }
  ) => {
    // Capture the current generation; ignore this request's callbacks if a
    // session reset happens before it resolves.
    const gen = genRef.current;
    if (opts.addBubble) {
      setMessages((prev) => [...prev, { role: "user", text: message }]);
    }
    setSending(true);

    sendMessage(
      {
        message,
        country: props.country,
        uiLanguage: getUiLanguageName(i18n.language),
        previousInteractionId: opts.freshContext
          ? undefined
          : previousInteractionId,
        webSearch,
      },
      {
        onSuccess: (res: ChatMessageResponse) => {
          if (gen !== genRef.current || !res) {
            return;
          }
          setKickoffFailed(false);
          setPreviousInteractionId(res.interactionId);
          if (res.chat) {
            setMessages((prev) => [
              ...prev,
              { role: "assistant", text: res.chat },
            ]);
          }
          // Non-empty article = draft generated.
          if (res.article && res.article.body) {
            setArticle(res.article);
            loadCategories(res.article);
          }
        },
        onError: (err) => {
          if (gen !== genRef.current) {
            return;
          }
          enqueueSnackbar(extractApiMessage(err, t("ai_chat.error")), {
            variant: "error",
          });
          if (opts.errorTarget === "kickoff") {
            setKickoffFailed(true);
          } else {
            // Mark the last user bubble as failed to show recovery actions.
            setMessages((prev) => {
              const idx = prev.map((m) => m.role).lastIndexOf("user");
              if (idx < 0) {
                return prev;
              }
              const copy = [...prev];
              copy[idx] = { ...copy[idx], error: true };
              return copy;
            });
          }
        },
        onEnd: () => {
          if (gen !== genRef.current) {
            return;
          }
          setSending(false);
        },
      }
    );
  };

  /**
   * Reset the whole conversation and start a fresh session (new kickoff).
   * Used as an escape hatch when retries keep failing.
   */
  const resetSession = () => {
    genRef.current += 1;
    setResetConfirmOpen(false);
    setMessages([]);
    setInput("");
    setArticle(null);
    setBusinessCategories([]);
    setCategoriesLoading(false);
    setKickoffFailed(false);
    setPreviousInteractionId(undefined);
    runSend(t("ai_chat.kickoff_message"), {
      addBubble: false,
      errorTarget: "kickoff",
      freshContext: true,
    });
  };

  /**
   * Send the current input to the chat API.
   */
  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || sending) {
      return;
    }
    setInput("");
    // A fresh send supersedes any pending error mark.
    setMessages((prev) =>
      prev.some((m) => m.error)
        ? prev.map((m) => (m.error ? { ...m, error: false } : m))
        : prev
    );
    runSend(trimmed, { addBubble: true, errorTarget: "bubble" });
  };

  // Index of the user bubble whose send failed (-1 when none).
  const erroredIndex = messages.findIndex((m) => m.error);

  /**
   * Resend the failed message, inheriting the (unchanged) previousInteractionId
   * so the conversation continues from the last good point.
   */
  const handleRetry = () => {
    if (erroredIndex < 0 || sending) {
      return;
    }
    const { text } = messages[erroredIndex];
    setMessages((prev) =>
      prev.map((m) => (m.error ? { ...m, error: false } : m))
    );
    runSend(text, { addBubble: false, errorTarget: "bubble" });
  };

  /**
   * Discard the failed message and restore its text to the input for editing.
   */
  const handleCancelErrored = () => {
    if (erroredIndex < 0 || sending) {
      return;
    }
    setInput(messages[erroredIndex].text);
    setMessages((prev) => prev.filter((_, i) => i !== erroredIndex));
  };

  // Automatically fire the kickoff message once on first landing so the AI
  // starts the hearing without the user having to type a trigger first.
  const kickoffSent = useRef(false);
  useEffect(() => {
    if (kickoffSent.current) {
      return;
    }
    kickoffSent.current = true;
    runSend(t("ai_chat.kickoff_message"), {
      addBubble: false,
      errorTarget: "kickoff",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Retry the kickoff message after its initial failure.
   */
  const handleKickoffRetry = () => {
    if (sending) {
      return;
    }
    setKickoffFailed(false);
    runSend(t("ai_chat.kickoff_message"), {
      addBubble: false,
      errorTarget: "kickoff",
    });
  };

  // Send on ⌘+Enter / Ctrl+Enter (Slack-style); plain Enter inserts a newline.
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  /**
   * Approve the draft and hand it off to the manual form via location.state.
   */
  const handleApprove = () => {
    if (!article) {
      return;
    }
    const preset: AiArticlePreset = {
      title: article.title,
      summary: article.summary,
      body: { version: "2.0.0", ...article.body },
      businessCategories,
    };
    setConfirmOpen(false);
    navigate(`/article/new/manual?country=${props.country}`, {
      state: { aiPreset: preset },
    });
  };

  const renderChatPanel = () => (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 && !sending && (
          <div className="mt-8 text-center text-sm text-gray-400">
            {t("ai_chat.start_hint")}
          </div>
        )}
        {messages.map((m, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Fragment key={i}>
            <div
              className={`flex ${m.error ? "mb-1" : "mb-2"} ${
                m.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2 text-sm ${
                  m.error
                    ? "border border-red-300 bg-red-50 text-gray-800"
                    : m.role === "user"
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {m.text}
              </div>
            </div>
            {m.error && !sending && (
              <div className="mb-3 flex flex-col items-end">
                <span className="mb-1 text-xs text-red-500">
                  ⚠ {t("ai_chat.send_failed")}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleRetry}
                    className="rounded-md border border-primary px-3 py-1 text-xs font-semibold text-primary transition-colors hover:border-button-hover hover:text-button-hover"
                  >
                    {t("ai_chat.retry")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelErrored}
                    className="rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-500 transition-colors hover:border-gray-400"
                  >
                    {t("ai_chat.cancel_edit")}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setResetConfirmOpen(true)}
                  className="mt-2 text-xs text-gray-400 underline transition-colors hover:text-gray-600"
                >
                  {t("ai_chat.reset")}
                </button>
              </div>
            )}
          </Fragment>
        ))}
        {sending && (
          <div className="mb-3 flex justify-start">
            <div className="flex items-center gap-2 rounded-2xl bg-gray-100 px-4 py-2 text-sm text-gray-500">
              <AutoAwesomeIcon className="text-primary" style={{ fontSize: 16 }} />
              <span className="flex gap-1">
                <span css={thinkingDot} />
                <span css={[thinkingDot, css({ animationDelay: "0.15s" })]} />
                <span css={[thinkingDot, css({ animationDelay: "0.3s" })]} />
              </span>
              <span>{thinkingMessages[thinkingIndex]}</span>
            </div>
          </div>
        )}
        {kickoffFailed && messages.length === 0 && !sending && (
          <div className="mt-8 flex flex-col items-center">
            <span className="mb-2 text-sm text-red-500">
              ⚠ {t("ai_chat.send_failed")}
            </span>
            <button
              type="button"
              onClick={handleKickoffRetry}
              className="rounded-md border border-primary px-4 py-1.5 text-sm font-semibold text-primary transition-colors hover:border-button-hover hover:text-button-hover"
            >
              {t("ai_chat.retry")}
            </button>
            <button
              type="button"
              onClick={() => setResetConfirmOpen(true)}
              className="mt-3 text-xs text-gray-400 underline transition-colors hover:text-gray-600"
            >
              {t("ai_chat.reset")}
            </button>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="border-t border-gray-200 p-3">
        {/* Web search toggle (ChatGPT-style); off by default */}
        <div className="mb-2 flex items-center">
          <button
            type="button"
            aria-pressed={webSearch}
            onClick={() => setWebSearch((prev) => !prev)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
              webSearch
                ? "border-primary bg-primary/10 text-primary"
                : "border-gray-300 bg-white text-gray-500 hover:border-gray-400"
            }`}
          >
            <PublicIcon style={{ fontSize: 16 }} />
            {t("ai_chat.web_search")}
          </button>
        </div>
        <div className="flex items-end gap-2">
          <textarea
            className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none"
            rows={2}
            placeholder={t("ai_chat.input_placeholder")}
            value={input}
            disabled={sending}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <IconButton
            color="primary"
            disabled={sending || input.trim().length === 0}
            onClick={handleSend}
            aria-label={t("ai_chat.send")}
          >
            <SendIcon />
          </IconButton>
        </div>
      </div>
    </div>
  );

  const renderPreviewPanel = () => (
    <div className="flex h-full min-h-0 flex-col bg-gray-50">
      {/* Scrollable article area */}
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
        <h2 className="mb-1 text-xl font-bold text-gray-900">
          {article?.title}
        </h2>
        {article?.summary && (
          <p className="mb-4 text-sm text-gray-600">{article.summary}</p>
        )}

        <div className="mb-2 text-xs font-bold uppercase text-gray-400">
          {t("ai_chat.preview.categories")}
        </div>
        <div className="mb-4 flex flex-wrap gap-2">
          {categoriesLoading ? (
            <CircularProgress size={18} />
          ) : businessCategories.length > 0 ? (
            businessCategories.map((c) => (
              <Chip key={c} label={c} size="small" variant="outlined" />
            ))
          ) : (
            <span className="text-xs text-gray-400">
              {t("ai_chat.preview.no_categories")}
            </span>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {(article?.body as any)?.blocks?.map((block: any, i: number) =>
            renderBlock(block, i)
          )}
        </div>
      </div>

      {/* Fixed action footer */}
      <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
        <div className="mb-3 rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-800">
          {t("ai_chat.image_notice")}
        </div>
        <Button
          text={t("ai_chat.approve")}
          isFullWidth
          disabled={categoriesLoading}
          onClick={() => setConfirmOpen(true)}
        />
        <div className="mt-2 text-xs text-gray-500">
          {categoriesLoading
            ? t("ai_chat.preview.categories_loading")
            : t("ai_chat.revise_hint")}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-[calc(100vh-64px)] min-w-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
        <AutoAwesomeIcon className="text-primary" />
        <h1 className="text-base font-bold text-gray-800">
          {t("ai_chat.heading")}
        </h1>
        <Chip
          label={t("create_press_release.select_method.ai.beta")}
          size="small"
          color="primary"
          className="!ml-1"
        />
        <button
          type="button"
          onClick={() => setResetConfirmOpen(true)}
          className="ml-auto inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-semibold text-gray-600 transition-colors hover:border-gray-400 hover:text-gray-800"
        >
          <RestartAltIcon style={{ fontSize: 16 }} />
          {t("ai_chat.reset")}
        </button>
      </div>

      {/* Beta notice: chat history is not persisted (dismissible) */}
      {historyNoticeOpen && (
        <div className="flex items-center gap-1.5 border-b border-yellow-200 bg-yellow-50 px-4 py-1.5 text-xs text-yellow-800">
          <InfoOutlinedIcon style={{ fontSize: 14 }} />
          <span className="flex-1">{t("ai_chat.history_notice")}</span>
          <button
            type="button"
            onClick={() => setHistoryNoticeOpen(false)}
            aria-label={t("dashboard.modal.button.cancel")}
            className="shrink-0 rounded p-0.5 text-yellow-700 transition-colors hover:bg-yellow-100"
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </button>
        </div>
      )}

      {article === null ? (
        <div className="flex-1 overflow-hidden">{renderChatPanel()}</div>
      ) : (
        <div className="grid flex-1 grid-cols-1 overflow-hidden md:grid-cols-2">
          <div className="min-h-0 border-b border-gray-200 md:border-b-0 md:border-r">
            {renderChatPanel()}
          </div>
          {renderPreviewPanel()}
        </div>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{t("ai_chat.confirm.title")}</DialogTitle>
        <DialogContent>
          <div className="text-sm text-gray-700">
            {t("ai_chat.image_notice")}
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button
            variant="outlined"
            text={t("dashboard.modal.button.cancel")}
            onClick={() => setConfirmOpen(false)}
          />
          <Button text={t("ai_chat.confirm.proceed")} onClick={handleApprove} />
        </DialogActions>
      </Dialog>

      <Dialog
        open={resetConfirmOpen}
        onClose={() => setResetConfirmOpen(false)}
      >
        <DialogTitle>{t("ai_chat.reset_confirm.title")}</DialogTitle>
        <DialogContent>
          <div className="text-sm text-gray-700">
            {t("ai_chat.reset_confirm.body")}
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button
            variant="outlined"
            text={t("dashboard.modal.button.cancel")}
            onClick={() => setResetConfirmOpen(false)}
          />
          <Button
            text={t("ai_chat.reset_confirm.proceed")}
            onClick={resetSession}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AiChatPage;
